// Card ⋮ Change Unit (M5-F9; rows M5.31–M5.33, DC-181), prototype cuOpen / cuPick. Allowed before the allotment
// letter, same project only. Left: Alotted Unit; right: Change To (project fixed, unit, payment plan, description); Old → New and
// what will be redone; the 15 days do not restart. Users who can approve Change Unit apply it at once
// ("Change Unit"); everyone else raises a request for their reporting manager ("Raise a request").
// The server lists the available units and does the change (GET /crm2/change-unit/:id/units, POST /crm2/change-unit/raise).
import React, { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import { useSelector } from "react-redux";
import Close from "../../Icons/Close";
import Button from "../../../Subcomponents/Button";
import { crm2ChangeUnitRaise, crm2ChangeUnitUnits } from "../../../Services/Crm2List";
import { getAllPlansByProjectFromFarvision } from "../../../Services/PaymentPlans";
import { dismissLoader, presentLoader } from "../../../Services/loader";
import { errorTost, successTost } from "../../../Services/Toast";
import { sdrDone, unitLine } from "../ui/cardParts";
import settingsCss from "../Settings/Crm2Settings.module.css";

const inr = (n) => `₹ ${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const diff = (a, b) => `${b - a >= 0 ? "+ " : "− "}${inr(Math.abs(b - a))}`;
const btnSec = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", fontSize: "1.4rem", lineHeight: "2rem" };
const btnPri = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", border: "1px solid #155EEF", fontSize: "1.4rem", lineHeight: "2rem" };
const lbl = { display: "block", fontSize: "1.4rem", fontWeight: 500, color: "#344054", margin: "1rem 0 .6rem" };
const hint = { fontSize: "1.3rem", color: "#667085" };
const field = { width: "100%", fontSize: "1.5rem", padding: ".9rem 1rem", border: "1px solid #D0D5DD", borderRadius: ".8rem", boxSizing: "border-box", color: "#101828", background: "#fff" };

function Kv({ rows }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(12rem,34%) 1fr", gap: ".2rem 1.2rem", fontSize: "1.4rem" }}>
      {rows.map(([a, b]) => (
        <React.Fragment key={a}>
          <span style={{ color: "#475467", padding: ".4rem 0", borderBottom: "1px solid #F2F4F7" }}>{a}</span>
          <span style={{ color: "#101828", fontWeight: 500, padding: ".4rem 0", borderBottom: "1px solid #F2F4F7" }}>{b}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// the user can apply it at once (today's ChangeUnit.jsx rule + the CRM 2.0 key)
const canApply = (u) => u?.roleId?.level == 1 || !!u?.roleId?.accessibleTabs?.["All Allotments"]?.["Change Unit Approver"] || !!u?.roleId?.accessibleTabs?.["CRM 2.0"]?.["Change Unit · Approve (apply directly)"]?.isAccessible;

export default function Crm2ChangeUnitModal({ card, onClose, onDone }) {
  const apId = card.allotmentPaymentId;
  const user = useSelector((s) => s.Auth.user);
  const appr = canApply(user);
  const [d, setD] = useState(null);
  const [pick, setPick] = useState("");
  const [why, setWhy] = useState("");
  const [err, setErr] = useState({});
  const [plans, setPlans] = useState(null); // FarVision plans of the project, as today's Change Plan
  const [planIdx, setPlanIdx] = useState("");

  useEffect(() => {
    crm2ChangeUnitUnits(apId)
      .then((r) => {
        console.log("[crm2][Crm2ChangeUnitModal] units", { allotmentPaymentId: apId, units: r?.units?.length, pending: !!r?.pending });
        setD(r);
        const bu = r?.current?.jdBuId ?? r?.current?.buId;
        if (bu)
          getAllPlansByProjectFromFarvision(bu, r.current.jdBuId ? false : undefined)
            .then((res) => {
              const list = res?.data?.result || [];
              setPlans(list);
              const k = list.findIndex((x) => r.current.plan && String(x.Id) === String(r.current.plan.Id));
              setPlanIdx(k >= 0 ? String(k) : "");
            })
            .catch((e) => {
              console.error("[crm2][Crm2ChangeUnitModal] plans failed", { allotmentPaymentId: apId, error: e?.message });
              setPlans([]);
            });
        else setPlans([]);
      })
      .catch((e) => {
        errorTost(e?.response?.data?.message || "Could not open Change Unit. Please try again.");
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apId]);

  const cur = d?.current || {};
  const u = (d?.units || []).find((x) => x.unitId === pick) || null;
  const redo = [
    "The customer confirms the new Application for Booking by OTP (the Application for Booking email is sent again)",
    card.isAgreementFormFilled ? "New review draft revision: every applicant confirms by OTP again" : "Review draft is made for the new unit",
    "AV & payment plan: back to CRM Review",
    card.isParkingSelectionSubmitted ? "Parking is reset and selected again" : "Parking is selected for the new unit",
    "Discount checks run again for the new unit",
    sdrDone(card) ? "Stamp duty & charges: the customer pays the difference, or the extra paid is refunded" : "Stamp duty & charges are worked out on the new price",
    "New cost sheet version",
  ];
  const ringText = card.ring?.started ? `Day ${card.ring.day} of ${card.ring.total}` : "not started";

  const submit = async () => {
    const e = { unit: !pick, why: !why.trim(), plan: !!plans?.length && planIdx === "" };
    setErr(e);
    if (e.unit || e.why || e.plan) return;
    presentLoader();
    try {
      const plan = planIdx !== "" && plans?.[Number(planIdx)] ? plans[Number(planIdx)] : null;
      const r = await crm2ChangeUnitRaise(apId, { unitId: pick, reason: why.trim(), plan });
      console.log("[crm2][Crm2ChangeUnitModal] done", { allotmentPaymentId: apId, status: r?.status });
      successTost(r?.status === "CHANGED" ? "Unit changed. Moved to CRM Review" : `Change Unit request sent to ${r?.approver || "your reporting manager"}`);
      onDone();
    } catch (x) {
      const message = x?.response?.data?.message || "Change Unit not saved. Please try again.";
      console.error("[crm2][Crm2ChangeUnitModal] failed", { allotmentPaymentId: apId, status: x?.response?.status, message });
      errorTost(message);
    } finally {
      dismissLoader();
    }
  };

  const blocked = d?.pending ? "A Change Unit request is already pending." : "";
  return (
    <Modal open onClose={onClose} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
      <Box className={settingsCss.modalWrapper} style={{ width: "88rem", maxWidth: "calc(100% - 3.2rem)" }}>
        <div className={settingsCss.modalHeader}>
          <span>Change Unit</span>
          <span className={settingsCss.modalClose} onClick={onClose} tabIndex={0} role="button" aria-label="Close">
            <Close width="1.2rem" height="1.2rem" />
          </span>
        </div>
        <div className={settingsCss.modalContent} style={{ maxHeight: "72vh", overflow: "auto" }}>
          {!d ? (
            <p style={{ ...hint, textAlign: "center", padding: "3rem 0" }}>Loading…</p>
          ) : blocked ? (
            <>
              <p style={{ fontSize: "1.5rem", fontWeight: 600, color: "#101828", marginBottom: ".8rem" }}>
                {unitLine(card)} · {card.leadName}
              </p>
              <div style={{ background: "#FFFAEB", border: "1px solid #FEDF89", color: "#B54708", borderRadius: ".8rem", padding: "1rem 1.4rem", fontSize: "1.4rem" }}>{blocked}</div>
            </>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(30rem,1fr))", gap: "2rem" }}>
                <div>
                  <p style={{ fontSize: "1.5rem", fontWeight: 600, color: "#101828", marginBottom: ".8rem" }}>Alotted Unit</p>
                  <Kv
                    rows={[
                      ["Project", cur.projectName || card.projectName],
                      ["Unit", unitLine(card)],
                      ["Unit type", cur.unitType || card.displayUnitType],
                      ["Agreement value", inr(cur.unitCost?.bsp)],
                      ["Payment plan", card.planName || "—"],
                    ]}
                  />
                </div>
                <div>
                  <p style={{ fontSize: "1.5rem", fontWeight: 600, color: "#101828", marginBottom: ".8rem" }}>Change To</p>
                  <label style={{ ...lbl, marginTop: 0 }}>Project</label>
                  <select style={field} disabled aria-label="Project">
                    <option>{cur.projectName || card.projectName}</option>
                  </select>
                  <p style={hint}>Same project only (Settings › CRM 2.0 › Days and rules).</p>
                  <label style={lbl} htmlFor="cu1">
                    Unit <sup style={{ color: "red" }}>*</sup>
                  </label>
                  <select
                    id="cu1"
                    style={field}
                    value={pick}
                    onChange={(e) => {
                      setPick(e.target.value);
                      setErr((x) => ({ ...x, unit: false }));
                    }}
                  >
                    <option value="">Select Unit No</option>
                    {d.units.map((x) => (
                      <option key={x.unitId} value={x.unitId}>
                        {[x.wingName, x.unitNo].filter(Boolean).join(" - ")} · Floor {x.floorNo} · {x.unitType} · {inr(x.unitCost.bsp)}
                      </option>
                    ))}
                  </select>
                  {err.unit && <p style={{ color: "#D92D20", fontSize: "1.3rem", margin: ".4rem 0 0" }}>Please select unit</p>}
                  <label style={lbl} htmlFor="cu2">
                    Payment Plan <sup style={{ color: "red" }}>*</sup>
                  </label>
                  <select
                    id="cu2"
                    style={field}
                    value={planIdx}
                    disabled={!plans}
                    onChange={(e) => {
                      setPlanIdx(e.target.value);
                      setErr((x) => ({ ...x, plan: false }));
                    }}
                  >
                    <option value="">{plans ? "Select Payment Plan" : "Loading…"}</option>
                    {(plans || []).map((x, i) => (
                      <option key={`${x.Id}-${i}`} value={String(i)}>
                        {x.Description}
                      </option>
                    ))}
                  </select>
                  {err.plan && <p style={{ color: "#D92D20", fontSize: "1.3rem", margin: ".4rem 0 0" }}>Please select payment plan</p>}
                  <p style={hint}>Pre-selected in CRM Review; the CRM approves the plan there.</p>
                  <label style={lbl} htmlFor="cu3">
                    Description <sup style={{ color: "red" }}>*</sup>
                  </label>
                  <textarea
                    id="cu3"
                    value={why}
                    onChange={(e) => {
                      setWhy(e.target.value);
                      setErr((x) => ({ ...x, why: false }));
                    }}
                    placeholder="Type..."
                    style={{ ...field, minHeight: "7rem" }}
                  />
                  {err.why && <p style={{ color: "#D92D20", fontSize: "1.3rem", margin: ".4rem 0 0" }}>Please enter description</p>}
                </div>
              </div>
              {u && (
                <div style={{ marginTop: "1.6rem", border: "1px solid #EAECF0", borderRadius: "1.2rem", padding: "1.2rem 1.6rem" }}>
                  <p style={{ fontSize: "1.5rem", fontWeight: 600, color: "#101828", marginBottom: ".6rem" }}>Old → New</p>
                  <Kv
                    rows={[
                      ["Unit", <>{unitLine(card)} → <b>{[u.wingName, u.unitNo].filter(Boolean).join(" - ")}</b></>],
                      ["Agreement value", <>{inr(cur.unitCost?.bsp)} → <b>{inr(u.unitCost.bsp)}</b> ({diff(cur.unitCost?.bsp || 0, u.unitCost.bsp)})</>],
                      ["Stamp duty & charges", `${diff(cur.unitCost?.sdr || 0, u.unitCost.sdr)}${sdrDone(card) ? (u.unitCost.sdr > (cur.unitCost?.sdr || 0) ? " · customer pays the difference" : u.unitCost.sdr < (cur.unitCost?.sdr || 0) ? " · refunded to the customer" : "") : ""}`],
                      ["Booking amount", u.bookingAmt ? `${inr(cur.bookingAmt?.total)} → ${inr(u.bookingAmt.total)} · payments already made move to the new unit (as today)` : "Booking amount of the new unit type; payments already made move to the new unit (as today)"],
                    ]}
                  />
                  <p style={{ fontSize: "1.5rem", fontWeight: 600, color: "#101828", margin: "1rem 0 .4rem" }}>What will be redone</p>
                  <ul style={{ ...hint, margin: "0 0 0 1.8rem" }}>
                    {redo.map((x) => (
                      <li key={x}>{x}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div style={{ background: "#EFF8FF", border: "1px solid #B2DDFF", color: "#175CD3", borderRadius: ".8rem", padding: "1rem 1.4rem", fontSize: "1.35rem", marginTop: "1.6rem" }}>
                Allowed only before the allotment letter. The 15 days do not restart (Day 0 stays {ringText}). {appr ? "You can approve Change Unit, so it is applied at once." : "It goes to your reporting manager in Approval Pending."}
              </div>
            </>
          )}
        </div>
        <div className={settingsCss.modalFooter}>
          <Button style={btnSec} btnName={blocked ? "Close" : "Cancel"} clicked={onClose} />
          {d && !blocked && <Button style={btnPri} btnName={appr ? "Change Unit" : "Raise a request"} clicked={submit} />}
        </div>
      </Box>
    </Modal>
  );
}
